import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow, CTABand } from "@/components/Shared";
import { CONSULTATION_COLUMNS, CONSULTATIONS, REGENERATIVE, THREADS, SKIN_LASER, MINOR_PROCEDURES, BODY } from "@/data/pricing";
import { getPricing } from "@/lib/api";

const STATIC_PRICING = {
  consultations: { columns: CONSULTATION_COLUMNS, rows: CONSULTATIONS },
  regenerative: REGENERATIVE,
  threads: THREADS,
  skin: SKIN_LASER,
  minor: MINOR_PROCEDURES,
  body: BODY,
};

const PriceTable = ({ columns, rows, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full min-w-[560px] border-collapse">
      <thead>
        <tr>
          <th className="text-left pb-4 border-b border-hairline/60" aria-label="Treatment" />
          {columns.map((c) => (
            <th key={c} className="text-right pb-4 border-b border-hairline/60 text-soft-grey text-[11px] tracking-[0.18em] uppercase font-medium whitespace-nowrap pl-6">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.name} data-testid={`price-row-${row.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <td className="py-4 border-b border-ink/8 text-ink/85 text-[15px] pr-6">
              {row.note ? (
                <a href="#skin-assessment-note" className="link-sea font-medium">{row.name}</a>
              ) : (
                row.name
              )}
              {row.sub && <span className="block text-soft-grey text-xs mt-0.5">{row.sub}</span>}
            </td>
            {row.all ? (
              <td colSpan={columns.length} className="py-4 border-b border-ink/8 text-right font-serif font-normal text-deep-sea text-lg">
                {row.all}
              </td>
            ) : (
              row.prices.map((p, i) => (
                <td key={i} className={`py-4 border-b border-ink/8 text-right whitespace-nowrap pl-6 ${row.tbc ? "text-soft-grey text-sm italic" : "font-serif font-normal text-deep-sea text-lg"}`}>
                  {row.tbc ? "To be confirmed" : p}
                </td>
              ))
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TableSection = ({ id, title, cream = false, table, note, children }) => (
  <section className={cream ? "bg-cream" : "bg-warm-white"} data-testid={`pricing-${id}`}>
    <div className="max-w-4xl mx-auto px-8 py-20">
      <Reveal>
        <h2 className="text-deep-sea mb-8">{title}</h2>
        {children}
        <PriceTable columns={table.columns} rows={table.rows} testId={`table-${id}`} />
        {note && <p className="text-soft-grey text-sm mt-5" data-testid={`${id}-note`}>{note}</p>}
      </Reveal>
    </div>
  </section>
);

const Hero = () => (
  <section className="relative h-[40vh] min-h-[320px] overflow-hidden flex items-end" data-testid="pricing-hero">
    <div className="absolute inset-0" aria-hidden="true">
      <img
        src="/images/lumiere-reception.webp"
        alt=""
        loading="eager"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: "50% 62%" }}
      />
      <div className="absolute inset-0 bg-[rgba(35,58,53,0.28)]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(35,58,53,0.55)] via-transparent to-[rgba(35,58,53,0.15)]" />
    </div>
    <div className="gold-frame inset-6 z-10" aria-hidden="true" />
    <div className="relative z-10 shell pb-14 w-full">
      <p className="eyebrow eyebrow-dark">Pricing</p>
      <h1 className="text-warm-white max-w-3xl" data-testid="pricing-heading">
        Pricing and payment
      </h1>
      <p className="standfirst text-mist max-w-2xl" data-testid="pricing-standfirst">
        Consultation fees and treatment pricing, stated plainly. Injectable treatments are priced in consultation,
        as Australian regulations require.
      </p>
    </div>
  </section>
);

const Pricing = () => {
  const [pricing, setPricing] = useState(STATIC_PRICING);

  useEffect(() => {
    getPricing().then((data) => setPricing((p) => ({ ...p, ...data }))).catch(() => {});
  }, []);

  return (
  <>
    <SEO title="Pricing & Payment | Lumière Aesthetics, Cottesloe" description="Consultation fees (redeemable within 30 days) and published pricing for regenerative, thread, skin and laser, minor-procedure and body treatments. Injectable treatments are priced in consultation, as Australian regulations require." />
    <Hero />

    {/* Section 1 — Consultations */}
    <section className="bg-warm-white" data-testid="pricing-consultations">
      <div className="max-w-4xl mx-auto px-8 py-20">
        <Reveal>
          <Eyebrow className="mb-4">Consultations</Eyebrow>
          <PriceTable columns={pricing.consultations.columns} rows={pricing.consultations.rows} testId="table-consultations" />
          <p className="text-soft-grey text-sm mt-5" data-testid="consultation-redeem-note">Every consultation fee is redeemable against treatment within 30 days.</p>
          <p className="text-ink/75 text-sm mt-3 leading-relaxed" data-testid="consultation-ladder-note">One fee applies to any physician consultation, whichever doctor you see. Thread lifting, revision work and hair transplantation are planned within the physician consultation.</p>
          <p className="text-soft-grey text-sm mt-3" data-testid="consultation-deposit-note">The deposit is taken when you book and credited to your consultation fee.</p>
          <p className="text-soft-grey text-sm mt-1">All new patients seeking injectable treatment have a consultation first.</p>
          <p id="skin-assessment-note" className="text-ink/70 text-sm mt-7 max-w-2xl leading-relaxed border-l-2 border-gold pl-5 scroll-mt-28" data-testid="skin-assessment-note">
            <span className="font-medium text-deep-sea">About the skin assessment.</span> A 45-minute medical
            assessment with our doctor with dedicated skin-medicine training — examination of your concern, a dermatoscopic check of
            anything that warrants one, a diagnosis, and a written skin plan. Book this if you have a spot, patch or
            change you'd like properly looked at before any treatment.
          </p>
        </Reveal>
      </div>
    </section>

    {/* Section 2 — Injectables (never priced) */}
    <section className="bg-warm-white pb-20" data-testid="pricing-injectables">
      <div className="max-w-4xl mx-auto px-8">
        <Reveal>
          <h2 className="text-deep-sea mb-8">Injectables</h2>
          <div className="bg-cream relative p-10 sm:p-12" data-testid="injectables-panel">
            <div className="gold-frame inset-3 opacity-70" aria-hidden="true" />
            <p className="text-ink/85 leading-[1.8] relative">
              Injectable treatments — anti-wrinkle treatment, dermal filler and facial balancing, collagen
              biostimulators, lips, jawline, tear trough, skin boosters, treatment for excessive sweating, and
              corrective work or dissolving — are priced in consultation. This is a requirement of Australian health
              advertising regulations, not a choice we've made to be vague: your doctor will assess what is appropriate
              for you, explain what it involves, and give you a written quote before anything is booked. You are under
              no obligation to proceed.
            </p>
          </div>
          <p className="text-soft-grey text-sm mt-5">
            A consultation is required before any first injectable treatment. See consultation fees above.
          </p>
          <Link to="/treatments/injectables" data-testid="injectables-link" className="link-sea inline-block mt-3 text-sm font-medium">
            Learn more about injectables at Lumière →
          </Link>
        </Reveal>
      </div>
    </section>

    {/* Section 3 — Rejuran and regenerative */}
    <TableSection id="regenerative" title="Rejuran and regenerative" cream table={pricing.regenerative}
      note="PRP and PRF are prepared from a small sample of your own blood. Courses apply to non-prescription treatments only." />

    {/* Section 4 — Threads */}
    <TableSection id="threads" title="Threads" table={pricing.threads}
      note="Thread lifting is planned and quoted in writing after a physician consultation with Dr Kristy Truong." />

    {/* Section 5 — Skin and laser */}
    <TableSection id="skin" title="Skin and laser" cream table={pricing.skin}
      note="Final pricing depends on the area treated and is confirmed at consultation. Items marked indicative are confirmed once the equipment is finalised." />

    {/* Section 6 — Minor procedures */}
    <TableSection id="minor" title="Minor procedures" table={pricing.minor}
      note="Pigmented lesions need a skin assessment with Dr Kiran Shahid first; removal is quoted from that assessment." />

    {/* Section 7 — Body and core-to-floor */}
    <TableSection id="body" title="Body and core-to-floor" cream table={pricing.body} />

    {/* Section 8 — Hair transplantation (quoted in consultation) */}
    <section className="bg-warm-white" data-testid="pricing-hair-transplantation">
      <div className="max-w-4xl mx-auto px-8 py-20">
        <Reveal>
          <h2 className="text-deep-sea mb-8">Hair transplantation</h2>
          <div className="bg-cream relative p-10 sm:p-12" data-testid="hair-transplant-panel">
            <div className="gold-frame inset-3 opacity-70" aria-hidden="true" />
            <p className="text-ink/85 leading-[1.8] relative">
              Hair transplantation and brow restoration with Dr Kristy Truong are quoted individually, in consultation.
              Every plan begins with a hair assessment — $250, redeemable against treatment within 30 days — and a
              written quote follows once the approach and graft count are agreed. No two plans are alike, so no single
              figure is published.
            </p>
          </div>
          <Link to="/hair/transplantation" data-testid="hair-transplant-link" className="link-sea inline-block mt-5 text-sm font-medium">
            About hair transplantation at Lumière →
          </Link>
        </Reveal>
      </div>
    </section>

    {/* Section 9 — Lumière Loyalty */}
    <section className="bg-cream" data-testid="pricing-loyalty">
      <div className="max-w-4xl mx-auto px-8 py-20">
        <Reveal>
          <h2 className="text-deep-sea mb-4">Lumière Loyalty</h2>
          <p className="text-ink/75 leading-relaxed max-w-2xl" data-testid="loyalty-intro">
            Every patient of the practice is enrolled automatically. There is nothing to sign up for and nothing to carry.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-8 mt-10">
            <div data-testid="loyalty-earn">
              <p className="font-serif font-light text-deep-sea text-4xl">1 point</p>
              <p className="text-ink/75 text-sm mt-3 leading-relaxed">for every $2 spent on consultations and on skin, laser, regenerative, thread, body and minor-procedure treatments.</p>
            </div>
            <div data-testid="loyalty-reward">
              <p className="font-serif font-light text-deep-sea text-4xl">$100</p>
              <p className="text-ink/75 text-sm mt-3 leading-relaxed">voucher at 1,000 points, redeemable against the same categories of treatment and consultation fees.</p>
            </div>
            <div data-testid="loyalty-tracking">
              <p className="font-serif font-light text-deep-sea text-4xl">Automatic</p>
              <p className="text-ink/75 text-sm mt-3 leading-relaxed">Points are recorded against your patient record in our booking system and shown at reception whenever you ask.</p>
            </div>
          </div>
          <p className="text-ink/70 text-sm mt-10 max-w-2xl leading-relaxed border-l-2 border-gold pl-5" data-testid="loyalty-rx-note">
            <span className="font-medium text-deep-sea">Injectable and other prescription treatments</span> earn no points and cannot be paid for with vouchers. That is a regulatory requirement, not a policy choice.
          </p>
          <Link to="/loyalty" data-testid="loyalty-terms-link" className="link-sea inline-block mt-6 text-sm font-medium">
            Read the programme terms →
          </Link>
        </Reveal>
      </div>
    </section>

    {/* Section 10 — Payment and booking */}
    <section className="bg-warm-white" data-testid="pricing-payment">
      <div className="max-w-4xl mx-auto px-8 py-20">
        <Reveal>
          <h2 className="text-deep-sea mb-10">Payment and booking</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-10">
            <div data-testid="payment-how-to-pay">
              <h3 className="text-deep-sea">How to pay</h3>
              <p className="text-ink/75 text-sm mt-3 leading-relaxed">Cards, EFTPOS and gift cards. No cash. No third-party finance offered.</p>
            </div>
            <div data-testid="payment-deposits">
              <h3 className="text-deep-sea">Deposits</h3>
              <p className="text-ink/75 text-sm mt-3 leading-relaxed">A deposit is taken when you book a consultation — $50 nurse, $100 physician — and credited to the consultation. Appointments of 60 minutes or longer carry a deposit, credited to the treatment.</p>
            </div>
            <div data-testid="payment-cancellations">
              <h3 className="text-deep-sea">Cancellations</h3>
              <p className="text-ink/75 text-sm mt-3 leading-relaxed">
                48 hours' notice. Our{" "}
                <Link to="/booking-and-cancellation-policy" className="link-sea font-medium" data-testid="cancellation-link">
                  booking and cancellation policy
                </Link>{" "}
                explains the detail plainly.
              </p>
            </div>
            <div data-testid="payment-gift-cards">
              <h3 className="text-deep-sea">Gift cards</h3>
              <p className="text-ink/75 text-sm mt-3 leading-relaxed">
                Available for skin, laser, regenerative, thread, body and minor-procedure treatments and for
                consultation fees. Not redeemable against injectable treatments.
              </p>
            </div>
          </div>
          <p className="text-soft-grey text-sm mt-12" data-testid="gst-note">All prices include GST. Prices apply from 1 October 2026.</p>
        </Reveal>
      </div>
    </section>

    <CTABand title="Start with a consultation" text="Consultation fees are redeemable against treatment within 30 days." />
  </>
  );
};

export default Pricing;
